import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  RewardsBalance,
  RewardCoupon,
  RewardCatalogItem,
  RedemptionResult,
  RedemptionHistoryItem,
  RetentionConsent,
} from '@/types'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const rewardsKeys = {
  balance: ['customer', 'rewards', 'balance'] as const,
  coupons: ['customer', 'rewards', 'coupons'] as const,
  catalog: ['customer', 'rewards', 'catalog'] as const,
  redemptions: (page: number) => ['customer', 'rewards', 'redemptions', page] as const,
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useRewardsBalance(enabled = true) {
  return useQuery({
    queryKey: rewardsKeys.balance,
    queryFn: async () => {
      const response = await api.get<{ data: RewardsBalance }>('/api/customer/rewards/balance')
      return response.data.data
    },
    enabled,
  })
}

export function useRewardCoupons() {
  return useQuery({
    queryKey: rewardsKeys.coupons,
    queryFn: async () => {
      const response = await api.get<{ data: RewardCoupon[] }>('/api/customer/rewards/coupons')
      return response.data.data
    },
  })
}

export function useRewardCatalog() {
  return useQuery({
    queryKey: rewardsKeys.catalog,
    queryFn: async () => {
      const response = await api.get<{ data: RewardCatalogItem[] }>('/api/customer/rewards/catalog')
      return response.data.data
    },
  })
}

interface RedemptionsResponse {
  data: RedemptionHistoryItem[]
  meta: { total_count: number; current_page: number; total_pages: number }
}

export function useRedemptions(page = 1) {
  return useQuery({
    queryKey: rewardsKeys.redemptions(page),
    queryFn: async () => {
      const response = await api.get<RedemptionsResponse>(
        `/api/customer/rewards/redemptions?page=${page}`,
      )
      return response.data
    },
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface RedeemPayload {
  reward_catalog_item_id: number
  idempotency_key: string
}

export function useRedeemReward() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: RedeemPayload) => {
      const response = await api.post<{ data: RedemptionResult }>(
        '/api/customer/rewards/redeem',
        payload,
      )
      return response.data.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rewardsKeys.balance })
      void queryClient.invalidateQueries({ queryKey: ['customer', 'rewards', 'redemptions'] })
    },
  })
}

export function useRetentionConsent() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<{ data: RetentionConsent }>(
        '/api/customer/consents/retention',
        { consent_source: 'liff' },
      )
      return response.data.data
    },
  })
}
